import React, { useEffect, useMemo, useRef } from "react";
import { PLOT_PAD_LEFT, PLOT_PAD_RIGHT, snapPrice } from "./utils";

// ---------------------------------------------------------------------------
// Interactive helpers — OptionStrat-style click-to-add + drag-strike.
// ---------------------------------------------------------------------------

const LEG_PALETTE = {
  "BUY-call":  { ring: "#00C805", bg: "#00C805", label: "B C" },
  "SELL-call": { ring: "#FF7A33", bg: "#FF7A33", label: "S C" },
  "BUY-put":   { ring: "#3FE5DC", bg: "#3FE5DC", label: "B P" },
  "SELL-put":  { ring: "#A98CFF", bg: "#A98CFF", label: "S P" },
  "BUY-stock": { ring: "#FFD60A", bg: "#FFD60A", label: "BS" },
  "SELL-stock":{ ring: "#FFD60A", bg: "#FFD60A", label: "SS" },
};

export const LegPickerPopover = ({ price, x, y, isMobile, onPick, onClose }) => {
  const sides = [
    { action: "BUY",  type: "call", color: "#00C805", label: "Buy Call" },
    { action: "SELL", type: "call", color: "#FF7A33", label: "Sell Call" },
    { action: "BUY",  type: "put",  color: "#3FE5DC", label: "Buy Put" },
    { action: "SELL", type: "put",  color: "#A98CFF", label: "Sell Put" },
  ];

  // Mobile (< 640px): render as a bottom sheet with full-width buttons +
  // tap-anywhere scrim. Desktop: anchored popover at the click site.
  if (isMobile) {
    return (
      <div
        className="fixed inset-0 z-40 flex items-end justify-center bg-black/65 backdrop-blur-sm"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={`Add leg at strike ${price}`}
        data-testid="payoff-leg-picker-sheet"
      >
        <div
          className="w-full bg-[#0a0a0c] border-t border-white/15 rounded-t-2xl p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="payoff-leg-picker"
        >
          <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mb-3" />
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                Add leg
              </div>
              <div className="text-base num text-white mt-0.5">
                @ ${Number(price).toFixed(2)}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="payoff-leg-picker-close"
              aria-label="Close leg picker"
              className="text-white/55 hover:text-white px-3 py-1.5 rounded border border-white/15 -mr-1"
            >
              Cancel
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {sides.map((s) => (
              <button
                key={`${s.action}-${s.type}`}
                type="button"
                onClick={() => onPick(s.action, s.type)}
                data-testid={`payoff-leg-pick-${s.action.toLowerCase()}-${s.type}`}
                aria-label={`${s.label} at strike ${price}`}
                className="text-[13px] font-medium px-3 py-3.5 rounded-lg border transition active:scale-95"
                style={{
                  color: s.color,
                  borderColor: `${s.color}55`,
                  background: `${s.color}12`,
                  minHeight: 48,   // WCAG 2.2 AA minimum touch target
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Desktop popover
  return (
    <>
      <div
        className="fixed inset-0 z-30"
        onClick={onClose}
        data-testid="payoff-leg-picker-scrim"
      />
      <div
        className="absolute z-40 bg-[#0a0a0c] border border-white/15 rounded-lg p-2.5 shadow-2xl"
        style={{
          left: Math.max(PLOT_PAD_LEFT + 4, Math.min(x - 80, 9999)),
          top: y,
          minWidth: 168,
        }}
        role="dialog"
        aria-label={`Add leg at strike ${price}`}
        data-testid="payoff-leg-picker"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5 flex items-center justify-between">
          <span>Add leg @ <span className="num text-white">${Number(price).toFixed(2)}</span></span>
          <button
            type="button"
            onClick={onClose}
            data-testid="payoff-leg-picker-close"
            aria-label="Close leg picker"
            className="text-white/45 hover:text-white"
          >
            ✕
          </button>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {sides.map((s) => (
            <button
              key={`${s.action}-${s.type}`}
              type="button"
              onClick={() => onPick(s.action, s.type)}
              data-testid={`payoff-leg-pick-${s.action.toLowerCase()}-${s.type}`}
              aria-label={`${s.label} at strike ${price}`}
              className="text-[11px] px-2 py-1.5 rounded border transition hover:scale-[1.02]"
              style={{
                color: s.color,
                borderColor: `${s.color}55`,
                background: `${s.color}10`,
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

// Live drag tooltip — follows the pointer with the current snapped strike.
export const DragLivePill = ({ clientX, clientY, strike }) => (
  <div
    className="fixed z-50 bg-[#FF7A33] text-black text-[12px] font-medium num px-2 py-1 rounded shadow-lg pointer-events-none whitespace-nowrap"
    style={{
      left: clientX,
      top: clientY - 36,
      transform: "translateX(-50%)",
    }}
    data-testid="payoff-drag-live-pill"
  >
    ${Number(strike).toFixed(2)}
  </div>
);

export const InteractiveLegOverlay = ({
  legs, activeMin, activeMax, chartHeight,
  listedStrikes,
  onLegStrikeChange, onLegRemove, draggingLegRef,
  setDragLive, focusedLegIdx, setFocusedLegIdx,
}) => {
  // We need to track the plot area's pixel width to convert price ↔ pixel.
  const overlayRef = useRef(null);

  // Visible legs (filter out stock legs — they have no strike to drag)
  const placedLegs = useMemo(
    () => (legs || [])
      .map((l, idx) => ({ ...l, _idx: idx }))
      .filter((l) => l.type !== "stock" && typeof l.strike === "number"),
    [legs],
  );

  const span = (activeMax || 0) - (activeMin || 0);

  const priceToPct = (price) => {
    if (!span) return 0;
    return Math.max(0, Math.min(100, ((price - activeMin) / span) * 100));
  };

  // Keyboard nudge: ← / → moves focused leg by one listed strike (or $0.50).
  // Pageup/PageDown moves by 10×.
  useEffect(() => {
    if (focusedLegIdx == null) return undefined;
    const onKey = (e) => {
      const leg = legs[focusedLegIdx];
      if (!leg || typeof leg.strike !== "number") return;
      let step = 0;
      if (e.key === "ArrowLeft") step = -1;
      else if (e.key === "ArrowRight") step = 1;
      else if (e.key === "PageDown") step = -10;
      else if (e.key === "PageUp") step = 10;
      else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        onLegRemove?.(focusedLegIdx);
        setFocusedLegIdx(null);
        return;
      }
      if (step === 0) return;
      e.preventDefault();
      let next;
      if (listedStrikes && listedStrikes.length) {
        // Step by index in listed strikes
        const sorted = [...listedStrikes].sort((a, b) => a - b);
        let idx = sorted.findIndex((s) => Math.abs(s - leg.strike) < 0.01);
        if (idx < 0) idx = sorted.findIndex((s) => s >= leg.strike);
        if (idx < 0) idx = sorted.length - 1;
        const target = Math.max(0, Math.min(sorted.length - 1, idx + step));
        next = sorted[target];
      } else {
        next = leg.strike + step * 0.5;
      }
      onLegStrikeChange?.(focusedLegIdx, next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusedLegIdx, legs, listedStrikes, onLegStrikeChange, onLegRemove, setFocusedLegIdx]);

  const handleDragStart = (e, leg) => {
    e.preventDefault();
    e.stopPropagation();
    draggingLegRef.current = leg._idx;
    setFocusedLegIdx(leg._idx);
    const overlay = overlayRef.current;
    if (!overlay) return;
    const rect = overlay.getBoundingClientRect();
    const plotLeft = rect.left + PLOT_PAD_LEFT;
    const plotWidth = rect.width - PLOT_PAD_LEFT - PLOT_PAD_RIGHT;
    if (plotWidth <= 0) return;
    // Lock page scroll on touch so the user can drag horizontally without
    // accidentally scrolling vertically.
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.touchAction = "none";

    const moveTo = (clientX, clientY) => {
      const px = (clientX ?? 0) - plotLeft;
      const pct = Math.max(0, Math.min(1, px / plotWidth));
      const raw = activeMin + pct * span;
      const next = snapPrice(raw, listedStrikes);
      onLegStrikeChange?.(leg._idx, next);
      setDragLive?.({
        idx: leg._idx, strike: next,
        clientX: clientX ?? plotLeft + px,
        clientY: clientY ?? rect.top + 20,
      });
    };

    const onMove = (mv) => {
      mv.preventDefault?.();
      const cx = mv.clientX ?? mv.touches?.[0]?.clientX;
      const cy = mv.clientY ?? mv.touches?.[0]?.clientY;
      if (cx == null) return;
      moveTo(cx, cy);
    };
    const onUp = () => {
      draggingLegRef.current = null;
      setDragLive?.(null);
      document.body.style.touchAction = prevTouchAction;
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
    document.addEventListener("pointermove", onMove, { passive: false });
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    // Set initial drag-live tooltip at the grip's current position
    moveTo(e.clientX ?? rect.left + (rect.width * priceToPct(leg.strike)) / 100,
           e.clientY ?? rect.top + 20);
  };

  if (!placedLegs.length) {
    return (
      <div
        ref={overlayRef}
        className="absolute inset-0 pointer-events-none"
        data-testid="payoff-interactive-overlay-empty"
      />
    );
  }

  return (
    <div
      ref={overlayRef}
      className="absolute inset-0 pointer-events-none"
      data-testid="payoff-interactive-overlay"
    >
      {placedLegs.map((leg) => {
        const pct = priceToPct(leg.strike);
        const palette = LEG_PALETTE[`${leg.action}-${leg.type}`] || LEG_PALETTE["BUY-call"];
        if (pct <= 0 || pct >= 100) return null;
        const leftCss = `calc(${PLOT_PAD_LEFT}px + (100% - ${PLOT_PAD_LEFT + PLOT_PAD_RIGHT}px) * ${pct} / 100)`;
        const isFocused = focusedLegIdx === leg._idx;
        return (
          <div
            key={`leg-line-${leg._idx}`}
            className="absolute pointer-events-none"
            style={{
              left: leftCss,
              top: 0,
              height: chartHeight,
              transform: "translateX(-50%)",
            }}
            data-testid={`payoff-leg-line-${leg._idx}`}
          >
            {/* Vertical line marker */}
            <div
              className="absolute top-2 bottom-2 left-1/2 -translate-x-1/2"
              style={{
                width: isFocused ? 2 : 1,
                background: palette.ring,
                opacity: isFocused ? 0.85 : 0.55,
                boxShadow: `0 0 ${isFocused ? 10 : 6}px ${palette.ring}${isFocused ? "99" : "66"}`,
              }}
            />
            {/* Grip cluster — split into a drag handle + a separate remove ×
                so touch users don't accidentally delete on drag. */}
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-auto flex items-center gap-0.5"
              style={{ touchAction: "none" }}
            >
              <button
                type="button"
                onPointerDown={(e) => handleDragStart(e, leg)}
                onFocus={() => setFocusedLegIdx(leg._idx)}
                onBlur={() => setFocusedLegIdx((cur) => (cur === leg._idx ? null : cur))}
                onKeyDown={(e) => {
                  // Make focus visible w/o triggering nav on the chart itself
                  if (e.key === " " || e.key === "Enter") e.preventDefault();
                }}
                data-testid={`payoff-leg-grip-${leg._idx}`}
                aria-label={`${leg.action} ${leg.type.toUpperCase()} at strike $${leg.strike}, ${leg.qty || 1} contract${leg.qty === 1 ? "" : "s"}, ${leg.dte || "?"} days. Drag to adjust strike, arrow keys to nudge, delete key to remove.`}
                role="slider"
                aria-valuemin={activeMin}
                aria-valuemax={activeMax}
                aria-valuenow={leg.strike}
                aria-valuetext={`$${leg.strike}`}
                className={
                  "cursor-ew-resize select-none rounded num font-medium tracking-wide whitespace-nowrap transition active:scale-95 " +
                  (isFocused
                    ? "ring-2 ring-white/70 ring-offset-1 ring-offset-[#0a0a0c]"
                    : "")
                }
                style={{
                  background: palette.bg,
                  color: "#050505",
                  boxShadow: `0 2px 8px ${palette.ring}55`,
                  // WCAG touch target — generous on touch, slim on hover.
                  padding: "6px 10px",
                  fontSize: 10.5,
                  minHeight: 28,
                }}
                title={`${leg.action} ${leg.type.toUpperCase()} $${leg.strike}`}
              >
                {palette.label} ${Math.round(leg.strike)}
                {leg.qty && leg.qty !== 1 && (
                  <span className="opacity-70 ml-0.5">×{leg.qty}</span>
                )}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onLegRemove?.(leg._idx);
                }}
                data-testid={`payoff-leg-remove-${leg._idx}`}
                aria-label={`Remove ${leg.action} ${leg.type} at $${leg.strike}`}
                className="rounded-full bg-black/70 text-white/85 hover:text-[#FF3B30] active:scale-90 transition"
                style={{
                  width: 18, height: 18,
                  fontSize: 11, lineHeight: 1,
                  border: "1px solid rgba(255,255,255,0.25)",
                }}
                title="Remove leg"
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default InteractiveLegOverlay;
